/**
 * The board's KPI tiles.
 *
 * Split out of DemandChrome for the 500-line ceiling. Every tile carries `why` —
 * the exact rule behind the number — because three of these were measuring the
 * wrong thing until Theresa checked them against the HDB, and a KPI nobody can
 * define is one nobody should act on.
 */
import { useState } from 'react';
import type { summarise } from './demandModel';

export function SummaryStrip({ stats }: { stats: ReturnType<typeof summarise> }) {
  // Every tile carries `why` — the exact rule behind the number. A KPI nobody
  // can define is one nobody should act on, and three of these were measuring
  // the wrong thing until Theresa checked them against the HDB.
  const tiles = [
    {
      k: 'Hired YTD',
      v: stats.hiredYtd,
      n: 'offers accepted this year',
      why: 'Roles whose status is hired and whose OFFER WAS ACCEPTED in this calendar year. Counted on the offer date, not the start date — a hire is won when it is signed.',
    },
    {
      k: 'Hired this trimester',
      v: stats.hiredTrimester,
      n: 'offers accepted since the trimester opened',
      why: 'The same rule, from the first month of this trimester onwards.',
    },
    {
      k: 'Q1 outlook',
      v: stats.lookahead,
      n: 'what this round collects',
      why: 'Roles sitting in the three lookahead months — the input this collection round exists to gather.',
    },
    {
      k: 'To recruit',
      v: stats.toRecruit,
      n:
        stats.undated > 0
          ? `${stats.drafts} drafts · ${stats.undated} with no date yet`
          : `${stats.toRecruit - stats.drafts} in the HDB · ${stats.drafts} drafts`,
      // No `delta` here on purpose. `toRecruit` ALREADY includes the drafts, so
      // a "+N" beside it read as drafts being added on top — the note below the
      // figure says what it is made of instead.

      why:
        'What TA still has to deliver: HDB roles that are OPEN or BACKLOGGED, plus every draft entered here. ' +
        'Excludes anyone already hired, excludes ON HOLD roles (paused, and re-dated when recruiting resumes), ' +
        'and excludes the Initiative application pipeline. Roles with no start date are INCLUDED — they are demand ' +
        'either way, and leaving them out made this disagree with the same filter run in Airtable.',
    },
    {
      k: 'Busiest month',
      v: stats.busiest?.n ?? 0,
      n: stats.busiest
        ? `starts in ${stats.busiest.month.label} ${stats.busiest.month.year}`
        : '—',
      why: 'The heaviest single month still ahead, counting the same roles as To recruit, placed on their ADJUSTED planned start date where one exists and their original planned date otherwise. Undated roles cannot land in a month and are excluded from this figure only.',
    },
  ];
  return (
    <div className="flex overflow-x-auto rounded-xl border border-gray-200 bg-white">
      {tiles.map((t) => (
        <div
          key={t.k}
          className="min-w-[152px] flex-1 border-r border-gray-100 p-3.5 last:border-r-0"
        >
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {t.k}
            <KpiInfo label={t.k} why={t.why} />
          </div>
          <div className="mt-0.5 text-2xl font-semibold tabular-nums text-gray-900">
            {t.v}
          </div>
          <div className="text-xs text-gray-500">{t.n}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * A unit's own sign-off state. Separate from the department's, because a
 * department has nine units and no single lead speaks for all of them.
 */

/**
 * A KPI's definition, on a small `i`.
 *
 * It hangs off the icon rather than the whole tile: a tile that turns the
 * cursor into a question mark reads as "this is unclear", which is the opposite
 * of what a KPI should say, and it fired wherever the pointer happened to rest.
 *
 * A real `<button>` with real state, not a bare `title`. `title` shows on hover
 * with a mouse and NOWHERE else — not on keyboard focus, not on a tap — so on a
 * touch screen or from the keyboard the definitions simply did not exist. Every
 * one of these figures is a definition somebody has to agree with before they
 * can use it, so reaching it must not depend on having a mouse.
 */
function KpiInfo({ label, why }: { label: string; why: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-expanded={open}
        aria-label={`What "${label}" counts`}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full border border-gray-300 text-[9px] font-bold normal-case leading-none text-gray-400 hover:border-gray-500 hover:text-gray-600 focus:border-gray-500 focus:outline-none"
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-5 z-20 w-64 -translate-x-1/2 rounded-md border border-gray-200 bg-white p-2 text-[11px] font-normal normal-case leading-snug tracking-normal text-gray-600 shadow-lg"
        >
          {why}
        </span>
      )}
    </span>
  );
}
